import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { AlertTriangle, CheckCircle, Clock, Banknote, Calendar, FileText, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

export default function ExchangeRateReconciliation() {
  const { hasPermission } = usePermissions();
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({
    actualRate: '',
    actualValue: '',
    actualRateDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [editingField, setEditingField] = useState(null);
  const [sortField, setSortField] = useState('InvoiceDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) { setSortDirection(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDirection('asc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  const sortedInvoices = useMemo(() => {
    return [...pendingInvoices].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (['OriginalAmount', 'ExchangeRate', 'DisplayAmount'].includes(sortField)) { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
      else if (sortField === 'InvoiceDate') { aVal = new Date(aVal || 0); bVal = new Date(bVal || 0); }
      else { aVal = String(aVal || '').toLowerCase(); bVal = String(bVal || '').toLowerCase(); }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [pendingInvoices, sortField, sortDirection]);

  const canManage = hasPermission('manage_finance_settings');

  useEffect(() => {
    if (canManage) {
      fetchPendingInvoices();
    }
  }, [canManage]);

  const fetchPendingInvoices = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/invoice-reconciliation/pending');
      setPendingInvoices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRate = async (e) => {
    e.preventDefault();
    
    if (!selectedInvoice) return;
    
    try {
      const response = await apiClient.post(
        `/invoice-reconciliation/${selectedInvoice.InvoiceID}/confirm-rate`,
        confirmData
      );
      
      alert(`✅ Exchange rate confirmed!\n\n` +
        `Invoice: ${response.data.invoiceNumber}\n` +
        `Old Rate: ${response.data.oldRate.toFixed(6)}\n` +
        `New Rate: ${response.data.newRate.toFixed(6)}\n` +
        `Old Amount: £${response.data.oldAmount.toFixed(2)}\n` +
        `New Amount: £${response.data.newAmount.toFixed(2)}\n` +
        `Difference: £${response.data.difference.toFixed(2)}`
      );
      
      setShowConfirmModal(false);
      setSelectedInvoice(null);
      setConfirmData({
        actualRate: '',
        actualValue: '',
        actualRateDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setEditingField(null);
      
      fetchPendingInvoices();
    } catch (error) {
      console.error('Failed to confirm exchange rate:', error);
      alert('❌ Failed to confirm exchange rate');
    }
  };

  const openConfirmModal = (invoice) => {
    setSelectedInvoice(invoice);
    const rate = parseFloat(invoice.ExchangeRate);
    const originalAmount = parseFloat(invoice.OriginalAmount);
    setConfirmData({
      actualRate: rate.toFixed(6),
      actualValue: (originalAmount / rate).toFixed(2),
      actualRateDate: new Date().toISOString().split('T')[0],
      notes: `Bank reconciliation for invoice ${invoice.InvoiceNumber}`
    });
    setEditingField(null);
    setShowConfirmModal(true);
  };

  const handleRateChange = (newRate) => {
    const rateVal = parseFloat(newRate);
    const originalAmount = parseFloat(selectedInvoice.OriginalAmount);
    setEditingField('rate');
    if (rateVal > 0 && !isNaN(rateVal)) {
      setConfirmData(prev => ({
        ...prev,
        actualRate: newRate,
        actualValue: (originalAmount / rateVal).toFixed(2)
      }));
    } else {
      setConfirmData(prev => ({ ...prev, actualRate: newRate }));
    }
  };

  const handleValueChange = (newValue) => {
    const valueVal = parseFloat(newValue);
    const originalAmount = parseFloat(selectedInvoice.OriginalAmount);
    setEditingField('value');
    if (valueVal > 0 && !isNaN(valueVal)) {
      setConfirmData(prev => ({
        ...prev,
        actualValue: newValue,
        actualRate: (originalAmount / valueVal).toFixed(6)
      }));
    } else {
      setConfirmData(prev => ({ ...prev, actualValue: newValue }));
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">You do not have permission to access exchange rate reconciliation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-6 h-6 text-orange-600" />
          <h1 className="text-2xl font-bold text-gray-900">Exchange Rate Reconciliation</h1>
        </div>
        <p className="text-gray-600">
          Confirm actual bank exchange rates for foreign currency invoices
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Confirmation</p>
              <p className="text-2xl font-bold text-orange-600">{pendingInvoices.length}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value (Estimated)</p>
              <p className="text-2xl font-bold text-gray-900">
                £{pendingInvoices.reduce((sum, inv) => sum + parseFloat(inv.DisplayAmount || 0), 0).toFixed(2)}
              </p>
            </div>
            <Banknote className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Original Currency Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {pendingInvoices.length > 0 && (
                  `${pendingInvoices[0]?.OriginalCurrency || 'USD'}${pendingInvoices.reduce((sum, inv) => sum + parseFloat(inv.OriginalAmount || 0), 0).toFixed(2)}`
                )}
              </p>
            </div>
            <FileText className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">How to Reconcile Exchange Rates:</h3>
        <ol className="list-decimal list-inside text-blue-800 space-y-1 text-sm">
          <li>Check your bank statement for the actual exchange rate used</li>
          <li>Click "Confirm Rate" on the invoice</li>
          <li>Enter the actual bank exchange rate</li>
          <li>System will recalculate invoice amounts and update budgets</li>
          <li>Invoice will be marked as confirmed</li>
        </ol>
      </div>

      {/* Pending Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Invoices Awaiting Bank Rate Confirmation</h2>
        </div>
        
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th onClick={() => handleSort('InvoiceNumber')} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center">Invoice # <SortIcon field="InvoiceNumber" /></span></th>
              <th onClick={() => handleSort('VendorName')} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center">Vendor <SortIcon field="VendorName" /></span></th>
              <th onClick={() => handleSort('DepartmentName')} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center">Department <SortIcon field="DepartmentName" /></span></th>
              <th onClick={() => handleSort('InvoiceDate')} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center">Date <SortIcon field="InvoiceDate" /></span></th>
              <th onClick={() => handleSort('OriginalAmount')} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center justify-end">Original Amount <SortIcon field="OriginalAmount" /></span></th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Est. Rate</th>
              <th onClick={() => handleSort('DisplayAmount')} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"><span className="inline-flex items-center justify-end">Est. Value (GBP) <SortIcon field="DisplayAmount" /></span></th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : sortedInvoices.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-4 text-center text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                    <p className="font-medium">No invoices pending reconciliation</p>
                    <p className="text-sm">All foreign currency invoices have confirmed bank rates</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedInvoices.map((invoice) => (
                <tr key={invoice.InvoiceID} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {invoice.Status === 'Approved' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          <CheckCircle className="w-3 h-3" /> Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                          <Clock className="w-3 h-3" /> {invoice.Status || 'Pending'}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                        <AlertTriangle className="w-3 h-3" /> Rate Pending
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.InvoiceNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {invoice.VendorName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {invoice.DepartmentName || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.InvoiceDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-right text-gray-900">
                    {invoice.OriginalCurrency}{parseFloat(invoice.OriginalAmount).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-right text-gray-500">
                    {parseFloat(invoice.ExchangeRate).toFixed(6)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-right text-gray-900">
                    £{parseFloat(invoice.DisplayAmount).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => openConfirmModal(invoice)}
                      className="btn-primary text-sm"
                    >
                      Confirm Rate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Confirm Rate Modal */}
      {showConfirmModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Confirm Bank Exchange Rate</h2>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Invoice Number:</p>
                  <p className="font-medium">{selectedInvoice.InvoiceNumber}</p>
                </div>
                <div>
                  <p className="text-gray-600">Original Amount:</p>
                  <p className="font-medium font-mono">
                    {selectedInvoice.OriginalCurrency}{parseFloat(selectedInvoice.OriginalAmount).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Current Est. Rate:</p>
                  <p className="font-medium font-mono">{parseFloat(selectedInvoice.ExchangeRate).toFixed(6)}</p>
                </div>
                <div>
                  <p className="text-gray-600">Current Est. Value:</p>
                  <p className="font-medium font-mono">£{parseFloat(selectedInvoice.DisplayAmount).toFixed(2)}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleConfirmRate}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Actual Bank Exchange Rate *
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={confirmData.actualRate}
                      onChange={(e) => handleRateChange(e.target.value)}
                      className={`input ${editingField === 'rate' ? 'ring-2 ring-blue-400' : ''}`}
                      placeholder="0.000000"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the exchange rate from your bank statement
                      <br />e.g. if 1 GBP = 1.3485 {selectedInvoice?.OriginalCurrency || 'USD'}, enter 1.3485
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Actual GBP Value *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">£</span>
                      <input
                        type="number"
                        step="0.01"
                        value={confirmData.actualValue}
                        onChange={(e) => handleValueChange(e.target.value)}
                        className={`input pl-7 ${editingField === 'value' ? 'ring-2 ring-blue-400' : ''}`}
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Or enter the GBP amount from your bank statement directly
                    </p>
                  </div>
                </div>

                {confirmData.actualRate && parseFloat(confirmData.actualRate) > 0 && confirmData.actualValue && parseFloat(confirmData.actualValue) > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-900 font-medium">New Confirmed Value:</p>
                        <p className="text-xl font-bold text-blue-900 font-mono">
                          £{parseFloat(confirmData.actualValue).toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-blue-900 font-medium">Difference from estimate:</p>
                        <p className={`text-lg font-bold font-mono ${
                          (parseFloat(confirmData.actualValue) - parseFloat(selectedInvoice.DisplayAmount)) >= 0 
                            ? 'text-red-600' : 'text-green-600'
                        }`}>
                          £{(parseFloat(confirmData.actualValue) - parseFloat(selectedInvoice.DisplayAmount)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmation Date
                  </label>
                  <input
                    type="date"
                    value={confirmData.actualRateDate}
                    onChange={(e) => setConfirmData({ ...confirmData, actualRateDate: e.target.value })}
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={confirmData.notes}
                    onChange={(e) => setConfirmData({ ...confirmData, notes: e.target.value })}
                    className="input"
                    rows="2"
                    placeholder="e.g., Bank statement ref #12345"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedInvoice(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Confirm & Recalculate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
